import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../../components/buttons/IconButton';
import { COLUMN_FILTER_TYPE_BY_KEY, COLUMN_LABEL_BY_KEY } from '../constants/columnFilterTypes';
import { isUnifiedFiltersEnabled } from '../../../../utils/constants/variables';
import ColumnFilterPopover from './ColumnFilterPopover';

/**
 * Small wrapper rendered inside each filterable column header.
 *
 * Owns: the ⌄ trigger button + the conditionally-rendered popover next to it.
 * Reads `state.openFilterColumn` and `state.columnFilters` directly so the
 * column-header JSX in index.jsx stays a single-line addition per column.
 *
 * Columns not in COLUMN_FILTER_TYPE_BY_KEY (e.g. Key end-markets, still
 * deferred) render nothing. Industry / Value chain / Products are registered
 * as KEYWORD_CLOUD; with unified filters the ▾ opens Add-filter at that
 * dimension instead of a per-column popover.
 */
const ColumnFilterTrigger = observer(({ columnKey, state }) => {
  if (!COLUMN_FILTER_TYPE_BY_KEY[columnKey]) return null;

  // Hide during the search loading animation so the column-header ⌄ buttons
  // don't appear before results land. Mirrors the result-count gate in
  // SearchResults/index.jsx (the `!isInitialLoading && !isLoading` check
  // wrapping the Spotlights bar and chip rail).
  if (state.isInitialLoading || state.isLoading) return null;

  const isOpen = state.openFilterColumn === columnKey;
  const isFiltered = !!state.columnFilters[columnKey];
  const label = COLUMN_LABEL_BY_KEY[columnKey] ?? columnKey;

  const togglePopover = (e) => {
    e?.stopPropagation?.();
    // Unified-filter model: the ▾ is a shortcut that opens the single Add-filter
    // panel jumped to this dimension, instead of a per-column popover.
    if (isUnifiedFiltersEnabled) {
      state.openAddFilterPanel(columnKey);
      return;
    }
    state.setOpenFilterColumn(isOpen ? null : columnKey);
  };

  return (
    <span className="column-filter-anchor">
      <IconButton
        icon={ICON_BUTTON_ICONS.CHEVRON_DOWN}
        // V1.5 — color logic owned by IconButton's variant system. Default
        // state is muted (DEFAULT), hover/active uses the BLUE variant which
        // ships its own `--btn-icon-filter-white` filter so the chevron flips
        // to white on the action-blue background.
        type={isFiltered ? ICON_BUTTON_TYPES.BLUE : ICON_BUTTON_TYPES.DEFAULT}
        hoverType={ICON_BUTTON_TYPES.BLUE}
        withBorder={false}
        filled={isFiltered}
        onClick={togglePopover}
        tooltipText={isOpen ? '' : `Filter ${label}`}
        tooltipPosition="bottom-center"
        wrapClassName="column-filter-trigger"
      />
      {isOpen && !isUnifiedFiltersEnabled && (
        <ColumnFilterPopover
          columnKey={columnKey}
          currentValue={state.columnFilters[columnKey]}
          // Popover distributions (numeric extent, multi-select counts,
          // country list) operate on the displayable set, so excluded
          // companies don't leak into the analyst's filter choices.
          results={state.results.filter((c) => !c.isExcluded)}
          columnFilters={state.columnFilters}
          activeSpotlights={state.activeSpotlights}
          onApply={(val) => state.setColumnFilter(columnKey, val)}
          onClear={() => state.clearColumnFilter(columnKey)}
          onClose={() => state.setOpenFilterColumn(null)}
        />
      )}
    </span>
  );
});

ColumnFilterTrigger.propTypes = {
  columnKey: PropTypes.string.isRequired,
  state: PropTypes.object.isRequired
};

export default ColumnFilterTrigger;
